import { Link } from 'react-router-dom';
import { Card, Badge } from '@rackd/ui';
import {
  IconUser,
  IconBarbell,
  IconCalendar,
  IconChevronRight,
  IconCrown,
  IconShield,
} from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import type { AdminUser } from '../api';

interface UserCardProps {
  user: AdminUser;
}

const tierColors = {
  free: 'bg-[var(--text-tertiary)]/10 text-[var(--text-tertiary)]',
  power_rack: 'bg-[var(--pr-gold)]/10 text-[var(--pr-gold)]',
  trial: 'bg-[var(--info)]/10 text-[var(--info)]',
};

const tierLabels = {
  free: 'Free',
  power_rack: 'Power Rack',
  trial: 'Trial',
};

export function UserCard({ user }: UserCardProps) {
  return (
    <Link to={`/users/${user.id}`}>
      <Card className="p-4 hover:border-[var(--accent-primary)]/30 transition-colors">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-[var(--background-tertiary)] flex items-center justify-center flex-shrink-0">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName || ''}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <IconUser size={24} className="text-[var(--text-tertiary)]" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-[var(--text)] truncate">
                {user.displayName || 'No name'}
              </h3>
              {user.isAdmin && (
                <Badge className="admin-badge">
                  <IconShield size={12} />
                  Admin
                </Badge>
              )}
              <Badge className={tierColors[user.tier]}>
                {user.tier === 'power_rack' && <IconCrown size={12} />}
                {tierLabels[user.tier]}
              </Badge>
            </div>

            <p className="text-sm text-[var(--text-tertiary)] truncate mb-2">
              {user.email || 'No email'}
            </p>

            {/* Stats */}
            <div className="flex items-center gap-4 text-xs text-[var(--text-tertiary)]">
              <span className="flex items-center gap-1">
                <IconBarbell size={14} />
                {user.workoutCount} workouts
              </span>
              <span className="flex items-center gap-1">
                <IconCalendar size={14} />
                Joined {formatDistanceToNow(user.createdAt, { addSuffix: true })}
              </span>
              {user.lastActiveAt && (
                <span>
                  Active {formatDistanceToNow(user.lastActiveAt, { addSuffix: true })}
                </span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <IconChevronRight size={20} className="text-[var(--text-tertiary)] flex-shrink-0" />
        </div>
      </Card>
    </Link>
  );
}
